import { PageLayout } from '@repo/ui/components/page-layout'
import { QueryBreadcrumbs } from '@repo/ui/components/query-breadcrumbs'
import { createFileRoute } from '@tanstack/react-router'
import { NodeDetails } from '@/modules/file-manager/components/details-node'
import { DetailsPanel } from '@/modules/file-manager/components/details-panel'
import { FilePreview } from '@/modules/file-manager/components/file-preview'
import { ListingProvider } from '@/modules/file-manager/hooks/use-listing'
import { useCursorList } from '@/modules/file-manager/query/use-cursor-list'
import { GlobalSearch } from '@/modules/search/components/global-search'
import { SearchResultsBar } from '@/routes/(private)/(shell)/search/-components/search-results-bar'
import { SearchResultsList } from '@/routes/(private)/(shell)/search/-components/search-results-list'
import {
	searchResultsSearchMiddlewares,
	validateSearchResultsSearch
} from '@/routes/(private)/(shell)/search/-lib/search-results-address'
import { searchResultsQueryOptions } from '@/routes/(private)/(shell)/search/-query/query-options'

export const Route = createFileRoute('/(private)/(shell)/search/')({
	component: SearchResultsPage,
	// Canonical on the way out, forgiving on the way in: OPSSTD-6606, and
	// `search-results-address.ts` holds the whole of why they are two halves.
	search: { middlewares: searchResultsSearchMiddlewares },
	staticData: {
		breadcrumb: 'client:search.results_page.title'
	},
	validateSearch: validateSearchResultsSearch
})

/**
 * «Tutti i risultati», and it is a listing page like the other three.
 *
 * **The address is the whole of what makes the page shareable.** `q`, the scope
 * and the two filters live in it, in the canonical form
 * `search-results-address.ts` fixes, so a colleague who opens a copied link
 * gets the same search — narrowed, if anything, by their own permissions rather
 * than by anything this page decides. Nothing here asks who the caller is.
 *
 * **A one-step breadcrumb, not a path.** `staticData.breadcrumb` gives
 * `QueryBreadcrumbs` the same static key `SearchResultsHeader` used to render
 * as plain text — «Risultati di ricerca» — and it draws a single, non-linkable
 * step rather than a branch of the tree, because a result set sits in no
 * folder to have a path through. Where a hit *does* sit is a fact about the
 * row, and the list view gives it a column of its own.
 *
 * **`parentId` and `parentRole` are `null`, which is «there is no folder around
 * these rows» and not «I do not know yet».** Every hit comes out of a branch of
 * its own, so there is no one folder to take something out of — the same answer
 * «Condivisi con me» gives, for the same structural reason, and the move is
 * absent here exactly as it is there. The rest of the menu is not: renaming,
 * sharing, previewing and downloading are writes on the row itself, and the row
 * now carries the role that decides them.
 *
 * **Keyed on the search, so the selection empties when the results change.**
 * Leaving a term with rows picked and arriving at another term with them still
 * picked is a selection that no longer refers to anything on screen — the
 * folder pages key on the folder for the same reason. The key is the canonical
 * address the middlewares already produced, so it changes when and only when
 * the result set does.
 *
 * **The read happens here, once, and `OwnFolderPage` is the shape being
 * copied.** Two things need it — the provider, whose `nodes` the selection, the
 * drag, the viewer and the details panel all resolve identifiers against, and
 * the rows themselves — and `useCursorList` owns an effect: a cursor the server
 * refuses for a reason nobody caused resets the cache entry. Calling it in both
 * places would deduplicate the request and still leave that one effect with two
 * owners, which `architecture.md` refuses whether or not the duplicate reset is
 * harmless today. So the page reads and hands both halves down, exactly as the
 * folder page hands `useFolderChildren`'s.
 */
function SearchResultsPage() {
	const search = Route.useSearch()
	const { pages, query } = useCursorList(searchResultsQueryOptions(search))
	const results = pages?.flatMap((page) => page.items) ?? []

	return (
		<ListingProvider
			key={searchKey(search)}
			nodes={results}
			parentId={null}
			parentRole={null}
		>
			<PageLayout
				aside={
					<DetailsPanel>
						<NodeDetails />
					</DetailsPanel>
				}
				bar={<SearchResultsBar />}
				breadcrumb={<QueryBreadcrumbs />}
				search={<GlobalSearch />}
			>
				<SearchResultsList
					query={query}
					results={results}
					search={search}
				/>
			</PageLayout>

			<FilePreview />
		</ListingProvider>
	)
}

/**
 * What counts as «a different search» for the purpose of emptying the
 * selection.
 *
 * The same five values `searchResultsQueryOptions` keys its cache on, in the
 * same order, because the question is the same one: a change to any of them is
 * a different set of rows, and a change to none of them is not. Built from the
 * validated search rather than from `location.href`, so a link that arrived
 * with the parameters in another order — which the middlewares canonicalise on
 * the way out but not on the way in — does not read as a new search and throw
 * a selection away for nothing.
 */
function searchKey(search: ReturnType<typeof Route.useSearch>) {
	return [
		search.q.trim(),
		search.scope,
		search.scopeId ?? '',
		search.categories.join(','),
		search.people.join(',')
	].join('|')
}
